import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import PropTypes from "prop-types";
import { Spin } from "antd";
import {
  getApiBaseUrl,
  UPDATE_LOCATION,
  ADD_ACTIVITY,
  LOGIN,
} from "../../Config/Config";

const EditLocationType = ({ editObj }) => {
  const [formData, setFormData] = useState({
    BlockName: "",
    Description: "",
  });
  const [loading, setLoading] = useState(false);
  const [askPassword, setAskPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [error, setError] = useState("");
  const [changes, setChanges] = useState({});

  useEffect(() => {
    if (editObj) {
      setFormData({
        BlockName: editObj?.BlockName || "",
        Description: editObj?.Description || "",
      });
    }
  }, [editObj]);

  // const handleChange = (e) => {
  //   const { name, value } = e.target;
  //   setFormData((prev) => ({ ...prev, [name]: value }));
  //   setError(""); // Clear error on input change
  // };

  const fieldNameMap = {
    BlockName: "BlockName",
    Description: "Description",
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const newForm = { ...prev, [name]: value };
      const originalValue = editObj?.[name] || "";

      const mappedName = fieldNameMap[name] || name; // convert to FilterType/Description

      if (originalValue !== value) {
        setChanges((prevChanges) => ({
          ...prevChanges,
          [`Old${mappedName}`]: originalValue,
          [`New${mappedName}`]: value,
        }));
      } else {
        setChanges((prevChanges) => {
          const updated = { ...prevChanges };
          delete updated[`Old${mappedName}`];
          delete updated[`New${mappedName}`];
          return updated;
        });
      }

      return newForm;
    });
  };

  const handleAuth = async () => {
    if (!password) {
      setError("Please enter your password.");
      return;
    }

    setAuthLoading(true);
    setError("");

    const user = JSON.parse(sessionStorage.getItem("user") || "{}");

    try {
      if (!user?.UserName) {
        throw new Error("User name not found in session data.");
      }

      const payload = {
        username: user.UserName,
        password,
      };

      const res = await fetch(`${getApiBaseUrl()}${LOGIN}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.status) {
        setAskPassword(false);
        setPassword("");
        await performUpdate(); // Proceed with update
      } else {
        throw new Error(
          data.Message || "Invalid credentials. Please try again."
        );
      }
    } catch (err) {
      setError(err.message || "Unable to authenticate. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  const performUpdate = async () => {
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    const userId = user?.UserID || 0;

    const logActivity = async (isSuccess, notes) => {
      try {
        await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ActivityType: 1,
            PerformedBy: userId,
            PerformedOn: new Date()
              .toISOString()
              .slice(0, 19)
              .replace("T", " "),
            Notes: notes,
            Location: "updateBlock",
            IsSucces: isSuccess ? 1 : 0,
          }),
        });
      } catch (err) {
        console.error("Failed to log activity:", err);
      }
    };

    const finalNote = Object.entries(changes)
      .map(([key, value]) => `${key}: ${value}`)
      .join(", ");

    const payload = {
      BlockId: editObj.BlockId,
      BlockName: formData.BlockName,
      Description: formData.Description,
      UpdatedBy: userId,
      IsLocationType: 1,
    };

    try {
      setLoading(true);

      const response = await fetch(`${getApiBaseUrl()}${UPDATE_LOCATION}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      const status = result?.ResultData?.[0]?.Status;
      const message = "AHU type updated successfully.";

      if (response.ok && status === "Success") {
        await logActivity(true, `AHU type ${finalNote} updated successfully`);
        Swal.fire({ icon: "success", title: "Success", text: message }).then(
          () => {
            window.location.reload();
          }
        );
      } else {
        await logActivity(false, `AHU type ${finalNote} update failed`);
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: "AHU type update failed",
        });
      }
    } catch (error) {
      console.error("Network error:", error);
      await logActivity(false, `AHU type ${finalNote} update failed`);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to connect to server.",
      });
    } finally {
      setLoading(false);
      setAskPassword(false);
      setPassword("");
      setError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.BlockName || !formData.Description) {
      setError("Please fill in all required fields: AHU Type and Description.");
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please fill in all required fields: AHU Type and Description.",
      });
      return;
    }

    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    if (!user?.UserID) {
      setError("Invalid user data. Please log in again.");
      Swal.fire({
        icon: "error",
        title: "User Data Error",
        text: "Invalid user data. Please log in again.",
      });
      return;
    }

    setAskPassword(true); // Trigger reauthentication
  };

  return (
    <>
      {(loading || authLoading) && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 1050,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
          }}
        >
          <Spin size="large" tip="Processing..." />
        </div>
      )}
      <div
        className="offcanvas offcanvas-end"
        tabIndex="-1"
        id="EditBlocksOffcanvas"
      >
        <form onSubmit={handleSubmit}>
          <div className="offcanvas-header">
            <h5 className="offcanvas-title">Edit AHU Type</h5>
            <div className="d-flex align-items-center ms-auto">
              <button
                type="submit"
                className="btn btn-sm btn-outline-success me-2"
                title="Save"
                disabled={askPassword}
              >
                <i className="fa fa-check fs-4"></i>
              </button>
              <button
                type="button"
                className="btn-close text-reset"
                data-bs-dismiss="offcanvas"
                aria-label="Close"
                disabled={askPassword}
              ></button>
            </div>
          </div>
          <div className="offcanvas-body">
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}
            <div className="mb-3">
              <label className="form-label">AHU Type</label>
              <span className="text-danger ms-1">*</span>
              <input
                type="text"
                name="BlockName"
                value={formData.BlockName}
                onChange={handleChange}
                className="form-control"
                required
                disabled={askPassword}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Description</label>
              <span className="text-danger ms-1">*</span>
              <textarea
                name="Description"
                value={formData.Description}
                onChange={handleChange}
                className="form-control"
                required
                disabled={askPassword}
              ></textarea>
            </div>
            {askPassword && (
              <div className="mt-2">
                <div className="mb-3 w-lg-75 m-auto">
                  <label className="form-label">
                    Confirm Password <span className="text-danger"> *</span>
                  </label>
                  <div className="pass-group">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className="pass-input form-control"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setError("");
                      }}
                      onCopy={(e) => e.preventDefault()}
                      onPaste={(e) => e.preventDefault()}
                      onCut={(e) => e.preventDefault()}
                      placeholder="Enter Password"
                    />
                    <span
                      className={`ti toggle-password ${
                        showPassword ? "ti-eye" : "ti-eye-off"
                      }`}
                      onClick={() => setShowPassword(!showPassword)}
                    ></span>
                  </div>
                </div>
                <button
                  className="btn btn-primary btn-sm me-2"
                  onClick={handleAuth}
                  disabled={authLoading || !password}
                >
                  {authLoading ? "Verifying..." : "Confirm"}
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setAskPassword(false);
                    setPassword("");
                    setError("");
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

EditLocationType.propTypes = {
  editObj: PropTypes.object,
};

export default EditLocationType;
